import React, { useState } from "react";
import PropTypes from "prop-types";
import { lowerCase } from "lodash";
import styled from "styled-components";
import moment from "moment";
import { Tooltip as ReactTooltip } from "react-tooltip";
import { getClassName } from "./SupportTicketHeader";

import {
  formatDate,
  getDifferenceBetweenDatesSpecific
} from "../../helpers/date";

import Badge from "../common/Badge";

import "./SupportTicket.scss";

const getBackgroundColor = (
  isDragging,
  isGroupedOver,
  authorColors = { soft: "#FFFFFF" }
) => {
  if (isDragging) {
    return authorColors.soft;
  }

  if (isGroupedOver) {
    return "#EBECF0";
  }

  return "#FFFFFF";
};

const getBorderColor = (isDragging, authorColors = { hard: "#d7d7d7" }) =>
  isDragging ? authorColors.hard : "transparent";

const Container = styled.a`
  border: 2px solid transparent;
  border-color: ${(props) => getBorderColor(props.isDragging, props.colors)};
  background-color: ${(props) =>
    getBackgroundColor(props.isDragging, props.isGroupedOver, props.colors)};
  box-shadow: ${({ isDragging }) =>
    isDragging ? `2px 2px 1px #A5ADBA` : "none"};
  padding: 8px;
  min-height: 40px;
  user-select: none;
  border-radius: 5px;
  /* anchor overrides */
  color: #091e42;

  &:hover,
  &:active {
    color: #091e42;
    text-decoration: none;
  }

  &:focus {
    outline: none;
    border-color: ${(props) => props?.colors?.hard || "#2684FF"};
    box-shadow: none;
  }

  /* flexbox */
  display: flex;
  height: 100%;
`;
const getDifferenceBetweenDates = (start, end) => {
  const startTime = moment(start);
  const endTime = moment(end);
  const duration = moment.duration(endTime.diff(startTime));
  const hours = parseInt(duration.asHours());
  const minutes = parseInt(duration.asMinutes()) % 60;

  const hourString = Math.abs(hours) ? `${Math.abs(hours)} hr` : "";

  const minuteString = Math.abs(minutes) ? `${Math.abs(minutes)} min` : "";
  return `${hourString} ${
    hourString && minuteString ? "and" : ""
  } ${minuteString || ""}`;
};
const SupportTicketItem = ({
  item,
  isDragging,
  isGroupedOver,
  provided,
  name,
  auth,
  onClick
}) => {
  const [isTooltipOpenOnHover, setIsTooltipOpenOnHover] = useState(false);
  const [isTooltipOpenOnClick, setIsTooltipOpenOnClick] = useState(false);

  const getSpecificDifference = (value) => {
    const difference = getDifferenceBetweenDatesSpecific(
      new Date(value),
      new Date()
    );
    const isNegative = difference[0] === "-";
    return (
      <span style={{ color: isNegative ? "red" : "black" }}>{difference}</span>
    );
  };

  return (
    <Container
      ref={provided.innerRef}
      colors={item?.author?.colors}
      href={item?.author?.url}
      isDragging={isDragging}
      isGroupedOver={isGroupedOver}
      name={name}
      onClick={() => onClick(item)}
      {...provided.draggableProps}
      {...provided.dragHandleProps}
    >
      <div className="test-info-item">
        <div style={{ width: "100%", marginBottom: "1rem" }}>
          <Badge
            className={`ticket ${getClassName(
              lowerCase(item.request_priority)
            )}`}
            display="block"
          >
            Priority:&nbsp;{item.request_priority}
          </Badge>
          {item?.project?.contract?.tier?.label && (
            <Badge
              className={`ticket ${getClassName(
                lowerCase(item.request_priority)
              )}`}
              display="block"
            >
              Priority:&nbsp;{item.request_priority}
            </Badge>
          )}
        </div>
        <p
          className="ticket-description"
          data-tooltip-content={item.request_nature_details}
          data-tooltip-variant={auth.isNightMode ? "dark" : "light"}
          id={`${item._id}-description`}
          role="presentation"
          onKeyDown={() => {}}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsTooltipOpenOnClick((prev) => !prev);
          }}
          onMouseEnter={() => setIsTooltipOpenOnHover(true)}
          onMouseLeave={() => setIsTooltipOpenOnHover(false)}
        >
          {item.request_nature_details}
        </p>
        {item.request_nature_details?.length > 98 && (
          <ReactTooltip
            anchorId={`${item._id}-description`}
            place="bottom"
            effect="solid"
            isOpen={isTooltipOpenOnClick || isTooltipOpenOnHover}
            type={auth.isNightMode ? "dark" : "light"}
            globalEventOff="click"
            className={`ticket-description-tooltip custom-tooltip ${
              auth.isNightMode ? "dark" : "light"
            }`}
            style={{
              maxHeight: 400,
              overflow: "auto",
              pointerEvents: "auto",
              maxWidth: 300
            }}
          />
        )}

        {(item.customer || item.requestor_company_name) && (
          <p className="simple-font">{`Customer: ${
            item.customer?.name || item.requestor_company_name
          }`}</p>
        )}
        {item.requestor_full_name && (
          <p className="simple-font">{`Requestor: ${item.requestor_full_name}`}</p>
        )}
        <p className="simple-font">{`Nature: ${item.request_nature}`}</p>
        {item.response_due && (
          <p className="simple-font">
            {formatDate(item.response_due, "MM/dd/yyyy hh:mm a")}
          </p>
        )}
        <div className="ticket-bottom-info">
          <div
            style={{
              display: "flex"
            }}
          >
            {item.location ? (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "flex-end",
                  color: "#50accb"
                }}
              >
                {item.location}
              </div>
            ) : (
              <null />
            )}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-end"
              }}
            >
              <Badge color="#f2f3f8" className="round" textColor>
                {item.support_ticket_number}
              </Badge>
            </div>
            {name === "In Process" && item.response_due ? (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "flex-end"
                }}
              >
                <Badge color="#f2f3f8" className="round" textColor>
                  {getSpecificDifference(item.response_due)}
                </Badge>
              </div>
            ) : (
              <null />
            )}
          </div>
          {item.assigned_to && item.assigned_to?.full_name && (
            <span
              className={`assigned-to ${
                item.assigned_to ? "assigned" : "unassigned"
              }`}
              id={item._id}
              data-tooltip-variant={auth.isNightMode ? "dark" : "light"}
            >
              {item.assigned_to?.full_name
                ?.split(" ")
                .map((n) => n[0])
                ?.join("") || "NA"}
              <ReactTooltip
                anchorId={item._id}
                className={`custom-tooltip ${
                  auth.isNightMode ? "dark" : "light"
                }`}
                place="bottom"
                effect="solid"
                type={auth.isNightMode ? "dark" : "light"}
                globalEventOff="click"
              >
                {(item.assigned_to && item.assigned_to?.full_name) ||
                  "Not Assigned"}
              </ReactTooltip>
            </span>
          )}
          {name === "Open" && (
            <span
              style={{ color: "red" }}
            >{`Open for ${getDifferenceBetweenDates(
              item.created_on,
              new Date()
            )}`}</span>
          )}
        </div>
      </div>
    </Container>
  );
};

SupportTicketItem.propTypes = {
  item: PropTypes.object,
  isDragging: PropTypes.bool,
  isGroupedOver: PropTypes.bool,
  provided: PropTypes.object,
  name: PropTypes.string.isRequired,
  auth: PropTypes.object.isRequired,
  onClick: PropTypes.func
};

SupportTicketItem.defaultProps = {
  item: {},
  isDragging: false,
  isGroupedOver: false,
  provided: {},
  onClick: () => null
};

export default SupportTicketItem;
